import React from 'react';

import dayjs from 'dayjs';
import groupBy from 'lodash/groupBy';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type { ChatTraceExecutionRecord } from '~/services/TripPlannerService';

import { formatTraceDuration, formatTraceTime } from './chatTraceTiming';

interface Props {
  records?: ChatTraceExecutionRecord[];
}

function getMetadata(record: ChatTraceExecutionRecord): Record<string, unknown> {
  return record.metadata && typeof record.metadata === 'object' && !Array.isArray(record.metadata)
    ? (record.metadata as Record<string, unknown>)
    : {};
}

export default function ChatTraceExecutions({ records = [] }: Props) {
  if (!records.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        Timing unavailable for this turn.
      </Typography>
    );
  }

  const executions = Object.entries(groupBy(records, 'executionId'));
  return (
    <Stack spacing={1}>
      {executions.map(([executionId, executionRecords], index) => {
        const sorted = [...executionRecords].sort(
          (a, b) => dayjs(a.startedAt).valueOf() - dayjs(b.startedAt).valueOf(),
        );
        const root = sorted.find((record) => record.kind === 'turn');
        const metadata = root ? getMetadata(root) : {};
        const label = metadata.resumed ? 'Resume' : metadata.regenerated ? 'Regeneration' : 'Execution';
        const startedAt = root?.startedAt ?? sorted[0].startedAt;
        return (
          <Accordion key={executionId} disableGutters variant="outlined" sx={{ '&:before': { display: 'none' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              id={`execution-${executionId}-header`}
              aria-controls={`execution-${executionId}-content`}
            >
              <Typography variant="subtitle2">
                {label} {index + 1} · {root ? formatTraceDuration(root.durationMs) : 'Timing incomplete'} ·{' '}
                {root?.status ?? 'Root record unavailable'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails id={`execution-${executionId}-content`}>
              <Typography variant="caption" color="text.secondary">
                Started {formatTraceTime(startedAt)}. Steps can overlap; their durations do not add up to the total
                elapsed time.
              </Typography>
              <TableContainer>
                <Table size="small" aria-label={`${label} ${index + 1} timing`}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Step</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Elapsed</TableCell>
                      <TableCell>Status / outcome</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sorted.map((record) => {
                      const details = getMetadata(record);
                      return (
                        <TableRow
                          key={record.id}
                          sx={{
                            bgcolor: record.status === 'failed' ? 'action.hover' : undefined,
                            '& td, & th': { verticalAlign: 'top' },
                          }}
                        >
                          <TableCell component="th" scope="row" sx={{ overflowWrap: 'anywhere', minWidth: 180 }}>
                            <Typography variant="body2">{record.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {record.kind}
                            </Typography>
                            {typeof details.streamStartedAt === 'string' && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                Stream: {formatTraceTime(details.streamStartedAt)} →{' '}
                                {typeof details.streamFinishedAt === 'string'
                                  ? formatTraceTime(details.streamFinishedAt)
                                  : 'Unfinished'}
                              </Typography>
                            )}
                            {typeof details.error === 'string' && (
                              <Typography variant="caption" display="block" color="error.main">
                                {details.error}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            +{formatTraceDuration(dayjs(record.startedAt).diff(dayjs(startedAt)))}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTraceDuration(record.durationMs)}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color={
                                record.status === 'failed'
                                  ? 'error.main'
                                  : record.status === 'completed'
                                    ? 'text.primary'
                                    : 'warning.main'
                              }
                            >
                              {record.status}
                            </Typography>
                            {details.outcome === 'returned_failure' && (
                              <Typography variant="caption" color="warning.main">
                                Returned success: false
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" color="text.secondary">
                HTTP attempts measure time to response headers; provider calls include stream consumption. Stream start
                is the first raw event, which may precede visible answer text. Unfinished records have no recorded end
                time.
              </Typography>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
}
